import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { FileServerService } from '@app/core/services/file-server.service';
import { FileItem, FileService } from '@app/core/services/file.service';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { DocArcService } from '@app/services/doc-arc.service';
import { GetPicturesParams, PictureService, PicturesGet } from '@app/services/picture.service';
import { PicturesListComponent } from './pictures-list/pictures-list.component';
import { catchError, EMPTY, from, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-pictures',
    templateUrl: './pictures.component.html',
    styleUrls: ['./pictures.component.css'],
    standalone: false,
})
export class PicturesComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetPicturesParams;

    @ViewChild(PicturesListComponent)
    private picturesList?: PicturesListComponent;

    public page?: PicturesGet;

    public files: File[] = [];

    public uploading = false;

    private readonly params$ = new ReplaySubject<GetPicturesParams>(1);

    private readonly destroy$ = new Subject<void>();

    private fileNameSyntaxPromise?: Promise<FileNameSyntax>;

    constructor(
        private picture: PictureService,
        private docarc: DocArcService,
        private file: FileService,
        private fileServer: FileServerService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.params$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) =>
                    from(this.picture.getPictures(params)).pipe(
                        catchError((error) => {
                            console.error(error);
                            return EMPTY;
                        }),
                    ),
                ),
            )
            .subscribe((page) => this.initializePage(page));
    }

    public async upload() {
        if (!this.page || this.files.length === 0 || this.uploading) return;

        this.uploading = true;
        const progressDialog = this.modal.openProgressDialog({ indeterminate: true, max: this.files.length, value: 0, disableClose: true, label: 'preparing_files', translateLabel: true });

        try {
            const fileNameSyntax = await this.fileNameSyntaxPromise;
            if (!fileNameSyntax) throw Error('Filename syntax is not initialized');

            const preparedFiles = await Promise.all(this.files.map(this.prepareFileForUpload));
            progressDialog.setLabel('uploading_files', true);
            progressDialog.updateIndeterminate(false);
            await this.uploadPreparedFiles(preparedFiles, fileNameSyntax, progressDialog);

            this.files = [];
            progressDialog.updateStatus('finish');
            await this.picturesList?.reload();
        } catch (error) {
            console.error(error);
            progressDialog.updateStatus('invalid');
            progressDialog.updateInvalidText(error instanceof Error ? error.message : `${error}`);
        } finally {
            this.uploading = false;
        }
    }

    private prepareFileForUpload = async (file: File): Promise<PreparedPictureFile> => {
        if (!this.page) throw Error('Page must be initialized');

        const checksum = await this.file.hash(file);
        const { exists } = await this.docarc.checkFileExists({ filename: file.name, argtype: this.page.argtype, argvalue: this.page.argvalue, checksum_md5: checksum.hash });

        return { file, checksum_md5: checksum.hash, cancelUpload: exists };
    };

    private async uploadPreparedFiles(preparedFiles: PreparedPictureFile[], fileNameSyntax: FileNameSyntax, progressDialog: ProgressDialog): Promise<void> {
        for (let index = 0; index < preparedFiles.length; index++) {
            const preparedFile = preparedFiles[index];

            if (!preparedFile.cancelUpload) {
                const uploadedFile = await this.fileServer.uploadFile({ file: preparedFile.file, fileNameSyntax });
                if (!uploadedFile.fileItemKeyno || uploadedFile.fileItemKeyno <= 0) {
                    throw Error(uploadedFile.message || `Could not upload ${preparedFile.file.name}`);
                }

                await this.postprocessUploadedFile({ ...uploadedFile, checksum_md5: preparedFile.checksum_md5 });
            }

            progressDialog.updateValue(index + 1);
        }
    }

    private async postprocessUploadedFile(uploadedFile: UploadedPictureFile): Promise<void> {
        if (!uploadedFile.itemExists) {
            await this.addToDocumentArchive(uploadedFile);
            return;
        }

        if (!uploadedFile.updatedFileName) return;

        const existingPicture = this.picturesList?.pictures.find((picture) => picture.id === uploadedFile.fileItemKeyno);
        if (!existingPicture) {
            await this.addToDocumentArchive(uploadedFile);
            return;
        }

        const fileName = uploadedFile.fileName || '';
        existingPicture.fileName = fileName;
        await this.docarc.updateDocArcFile({
            docarcKeyno: existingPicture.item_id,
            docFileName: fileName,
            checksum: uploadedFile.checksum || existingPicture.checksum || '',
            checksum_md5: uploadedFile.checksum_md5,
        });
    }

    private addToDocumentArchive(data: UploadedPictureFile) {
        if (!this.page) throw Error('Page must be initialized');

        const docarc = {
            contentType: data.contentType || '',
            filename: data.fileName || '',
            localFileName: data.localFileName || '',
            originalFileName: data.originalFileName || '',
            originalTimestamp: data.originalDateTime || '',
            argType: this.page.argtype || '',
            argValue: this.page.argvalue || '',
            size: data.size || 0,
            checksum: data.checksum || '',
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            altitude: data.altitude || 0,
            heading: data.heading || 0,
            checksum_md5: data.checksum_md5,
        };

        return this.docarc.add(docarc);
    }

    public onDateRangeChange(): void {
        if (!this.page) return;
        void this.picturesList?.reload();
    }

    private initializePage(page: PicturesGet): void {
        this.page = page;
        this.navigator?.setHeading?.(page.page_heading);
    }

    private loadFileNameSyntax(params: GetPicturesParams): void {
        const request = this.file.getFileNameSyntax(params.argtype, params.argvalue);
        this.fileNameSyntaxPromise = request;
        void request.catch((error) => console.error(error));
    }

    private init(params: GetPicturesParams): void {
        this.params = params;
        this.page = undefined;
        this.loadFileNameSyntax(params);
        this.params$.next(params);
    }

    ngOnInit(): void {
        if (this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.init(this.navigator.params as unknown as GetPicturesParams);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']?.currentValue) {
            this.init(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.params$.complete();
    }
}

interface PreparedPictureFile {
    file: File;
    checksum_md5: string;
    cancelUpload: boolean;
}

interface UploadedPictureFile extends FileItem {
    checksum_md5: string;
}

interface FileNameSyntax {
    filenametemplate: string;
    localfilenametemplate: string;
}

type ProgressDialog = ReturnType<ModalService['openProgressDialog']>;
